
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { SYSTEM_NOTIFICATION_TYPES } from "@/constants/notificationTypes";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Listens for new system notifications via Supabase Realtime
 * and shows a toast to management users.
 */
export const useSystemNotifications = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isManagement = isAuthenticated && user?.role === "management";
  const shownIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isManagement) return;

    // Check for existing unread system notifications on mount
    const checkExisting = async () => {
      const { data } = await supabase
        .from('notifications')
        .select('id, message, type, created_at')
        .in('type', SYSTEM_NOTIFICATION_TYPES)
        .eq('archived', false)
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(5);

      if (data && data.length > 0) {
        data.forEach((notification) => {
          if (!shownIds.current.has(notification.id)) {
            shownIds.current.add(notification.id);
            showSystemToast(notification);
          }
        });
      }
    };

    checkExisting();

    // Listen for new system notifications in realtime
    const channel = supabase
      .channel('system-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        (payload) => {
          const newNotification = payload.new as any;
          if (
            SYSTEM_NOTIFICATION_TYPES.includes(newNotification.type) &&
            !shownIds.current.has(newNotification.id)
          ) {
            shownIds.current.add(newNotification.id);
            showSystemToast(newNotification);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isManagement]);

  const showSystemToast = (notification: { id: string; message: string; type: string }) => {
    const title = notification.type === 'user_registration'
      ? "Nieuwe registratie-aanvraag"
      : "Systeemmelding";

    toast(title, {
      description: notification.message || "Er is een nieuwe systeemmelding.",
      duration: 10000,
      action: {
        label: "Bekijken",
        onClick: () => navigate("/admin/registraties"),
      },
    });
  };
};
